<template>
  <app-modal-prompt
    width="700"
    :value="value"
    :loading="loading"
    @input="e => $emit('input', e)"
    @confirm="$emit('update', { 'show': show_copy, 'hide': hide_copy })"
    @cancel="$emit('cancel')"
  >
    <template v-if="$scopedSlots['header-actions']" #header-actions>
      <slot name="header-actions" :confirm="() => $emit('update', { 'show': show_copy, 'hide': hide_copy })" />
    </template>
    <template #title>
      {{ title }}
    </template>

    <template>
      <app-modal-container fluid>
        <app-modal-row v-if="$slots.top_actions">
          <app-modal-col cols="12">
            <slot name="top_actions"></slot>
          </app-modal-col>
        </app-modal-row>

        <!-- Listas y flechas -->
        <app-modal-row class="row-with-center-controls">
          <!-- Columna izquierda -->
          <app-modal-col cols="12" sm="5" class="pad-right">
            <v-list dense>
              <div class="list-header">
                <v-subheader class="text-color">{{ show_columns_header }}</v-subheader>
              </div>

              <!-- Filtro izquierdo solo si hay items -->
              <ColumnFilter v-if="show_copy.length" :items="show_copy" placeholder="Filtrar columnas visibles">
                <template v-slot:default="{ filteredItems }">
                  <draggable
                    v-model="show_copy"
                    :options="{ group: 'people' }"
                    style="min-height: 10px"
                    :class="['y-scroll', { 'no-scroll': !show_copy.length }]"
                  >
                    <template v-for="(item, index) in filteredItems">
                      <v-list-item
                        :key="item.value + '_' + index"
                        class="draggable-item pretty-item"
                        :class="{ 'pretty-item--selected': selectedShowItems.includes(item.value) && item.value !== 'ignore' }"
                        @click.stop="toggleShowSelection(item.value)"
                      >
                        <v-list-item-content>
                          <template v-if="item.value === 'ignore'">
                            <div class="text-color">
                              <b style="font-size:small">{{ indexer(index) + '. ' + item.text }}</b>
                              <v-btn
                                icon x-small color="primary"
                                style="right:-30px"
                                @click.stop="del_col(index)"
                              >
                                <v-icon>mdi-delete</v-icon>
                              </v-btn>
                            </div>
                          </template>
                          <template v-else>
                            <v-list-item-title
                              class="text-color"
                              v-text="indexer(index) + '. ' + item.text"
                            />
                          </template>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </draggable>

                  <!-- Botón seleccionar/deseleccionar (solo si hay ítems) -->
                  <div v-if="show_copy.length" class="list-actions">
                    <v-btn
                      small
                      outlined
                      class="toggle-all"
                      :class="{ 'toggle-all--active': isAllShowSelected }"
                      color="primary"
                      @click="toggleAllShow"
                    >
                      {{ isAllShowSelected ? 'Deseleccionar' : 'Seleccionar todo' }}
                    </v-btn>
                  </div>
                </template>
              </ColumnFilter>

              <!-- Si no hay items, mantiene altura vacía sin scroll -->
              <div v-else class="y-scroll no-scroll"></div>
            </v-list>
          </app-modal-col>

          <!-- Centro (flechas) -->
          <app-modal-col cols="12" sm="2" class="middle-col">
            <div class="middle-controls">
              <!-- ➡️ Solo si hay elementos en visibles -->
              <v-btn
                v-if="show_copy.length"
                icon small color="primary"
                :disabled="!selectedShowItems.some(item => item !== 'ignore')"
                class="ctrl-btn"
                @click="moveShowToHide"
                aria-label="Mover seleccionados a ocultas"
              >
                <v-icon>mdi-arrow-right-bold</v-icon>
              </v-btn>

              <!-- ⬅️ Solo si hay elementos en ocultas -->
              <v-btn
                v-if="hide_copy.length"
                icon small color="primary"
                :disabled="!selectedHideItems.length"
                class="ctrl-btn"
                @click="moveHideToShow"
                aria-label="Mover seleccionados a visibles"
              >
                <v-icon>mdi-arrow-left-bold</v-icon>
              </v-btn>
            </div>
          </app-modal-col>

          <!-- Columna derecha -->
          <app-modal-col cols="12" sm="5" class="pad-left">
            <v-list dense>
              <div class="list-header">
                <v-subheader class="text-color">{{ hide_columns_header }}</v-subheader>
              </div>

              <!-- Filtro derecho solo si hay items -->
              <ColumnFilter v-if="hide_copy.length" :items="hide_copy" placeholder="Filtrar columnas ocultas">
                <template v-slot:default="{ filteredItems }">
                  <draggable
                    v-model="hide_copy"
                    :options="{ group: 'people' }"
                    style="min-height: 10px"
                    :class="['y-scroll', { 'no-scroll': !hide_copy.length }]"
                  >
                    <template v-for="(item, idx) in filteredItems">
                      <v-list-item
                        :key="item.value + '_' + idx"
                        class="draggable-item pretty-item"
                        :class="{ 'pretty-item--selected': selectedHideItems.includes(item.value) }"
                        @click.stop="toggleHideSelection(item.value)"
                      >
                        <v-list-item-content>
                          <v-list-item-title class="text-color" v-text="item.text" />
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </draggable>

                  <!-- Botón seleccionar/deseleccionar (solo si hay ítems) -->
                  <div v-if="hide_copy.length" class="list-actions">
                    <v-btn
                      small
                      outlined
                      class="toggle-all"
                      :class="{ 'toggle-all--active': isAllHideSelected }"
                      color="primary"
                      @click="toggleAllHide"
                    >
                      {{ isAllHideSelected ? 'Deseleccionar' : 'Seleccionar todo' }}
                    </v-btn>
                  </div>
                </template>
              </ColumnFilter>

              <!-- Si no hay items, mantiene altura vacía sin scroll -->
              <div v-else class="y-scroll no-scroll"></div>
            </v-list>
          </app-modal-col>
        </app-modal-row>

        <!-- Añadir ignorar -->
        <app-modal-row>
          <app-modal-col cols="12">
            Añadir columna a ignorar
            <v-btn @click="add_ignore_col" outlined color="primary" small icon>
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </app-modal-col>
        </app-modal-row>
      </app-modal-container>
    </template>
  </app-modal-prompt>
</template>

<script>
import draggable from "vuedraggable";
import AppModalPrompt from '@/components/layout/AppModalPrompt.vue';
import AppModalContainer from '@/components/layout/AppContainer.vue';
import AppModalRow from '@/components/layout/AppRow.vue';
import AppModalCol from '@/components/layout/AppCol.vue';
import ColumnFilter from '@/components/ColumnFilter.vue';
import { DraggableShowHideDialog } from '@/modules/shared/domain/DraggableShowHideDialog';

export default {
  name: 'ExcelDraggableShowHideDialog',
  props: {
    value: { type: Boolean, required: true },
    title: { type: String, required: false },
    show_columns_header: { type: String, required: false, default: 'Columnas visibles en orden' },
    hide_columns_header: { type: String, required: false, default: 'Columnas ocultas' },
    show_columns: { type: Array, required: true },
    hide_columns: { type: Array, required: true },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      show_copy: [],
      hide_copy: [],
      selectedShowItems: [],
      selectedHideItems: [],
      dialogUtils: null,
    }
  },
  components: {
    AppModalContainer,
    AppModalRow,
    AppModalCol,
    AppModalPrompt,
    draggable,
    ColumnFilter,
  },
  created() {
    this.dialogUtils = new DraggableShowHideDialog([])
  },
  mounted() {
    this.show_copy = this.normalize(this.show_columns)
    this.hide_copy = this.normalize(this.hide_columns)
  },
  watch: {
    show_columns(newColumns) { this.show_copy = this.normalize(newColumns) },
    hide_columns(newColumns) { this.hide_copy = this.normalize(newColumns) },
    value(isOpen) {
      if (isOpen) {
        this.selectedShowItems = []
        this.selectedHideItems = []
      }
    }
  },
  computed: {
    isAllShowSelected() {
      const movableItems = this.show_copy.filter(column => column.value !== 'ignore').length
      if (!movableItems) return false
      return this.selectedShowItems.length === movableItems
    },
    isAllHideSelected() {
      if (!this.hide_copy.length) return false
      return this.selectedHideItems.length === this.hide_copy.length
    }
  },
  methods: {
    normalize(list) {
      return this.dialogUtils.normalizeColumns(list)
    },
    indexer(indexInput) {
      let currentValue = indexInput
      let indexLabel = ''
      indexLabel = (currentValue % 26 + 10).toString(36).toUpperCase() + indexLabel
      while (currentValue / 26 >= 1) {
        currentValue = parseInt(currentValue / 26)
        indexLabel = (currentValue % 26 + 9).toString(36).toUpperCase() + indexLabel
      }
      return indexLabel
    },
    add_ignore_col() {
      this.show_copy.push({ text: "IGNORAR", value: "ignore", width: 100 })
    },
    del_col(index) {
      this.show_copy.splice(index, 1)
    },
    toggleShowSelection(value) {
      if (value === 'ignore') return
      this.selectedShowItems = this.dialogUtils.toggleSelection(this.selectedShowItems, value)
    },
    toggleHideSelection(value) {
      this.selectedHideItems = this.dialogUtils.toggleSelection(this.selectedHideItems, value)
    },
    toggleAllShow() {
      const movableItems = this.show_copy.filter(column => column.value !== 'ignore')
      this.selectedShowItems = this.dialogUtils.toggleAll(movableItems, this.selectedShowItems)
    },
    toggleAllHide() {
      this.selectedHideItems = this.dialogUtils.toggleAll(this.hide_copy, this.selectedHideItems)
    },
    moveShowToHide() {
      if (!this.selectedShowItems.some(item => item !== 'ignore')) {
        this.selectedShowItems = []
        return
      }
      const { source, target } = this.dialogUtils.moveSelected(
        this.show_copy,
        this.hide_copy,
        this.selectedShowItems.filter(item => item !== 'ignore')
      )
      this.show_copy = source
      this.hide_copy = target
      this.selectedShowItems = []
    },
    moveHideToShow() {
      if (!this.selectedHideItems.length) return
      const { source, target } = this.dialogUtils.moveSelected(
        this.hide_copy,
        this.show_copy,
        this.selectedHideItems
      )
      this.hide_copy = source
      this.show_copy = target
      this.selectedHideItems = []
    },
  }
}
</script>

<style lang="scss" scoped>
.primary-bckground { background-color: var(--v-primary-base); }
.primary-border { border-color: var(--v-primary-base) !important; }

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.draggable-item {
  border-style: solid;
  border-right-style: solid;
  border-bottom-style: solid;
  border-color: var(--v-primary-base);
  border-width: thin;
}
.text-color { color: var(--v-primary-base); }

.y-scroll {
  overflow-y: auto;
  height: 300px;
}
.no-scroll {
  overflow-y: hidden;
}

.row-with-center-controls {
  display: flex;
  align-items: stretch;
}
.pad-right { padding-right: 8px; }
.pad-left  { padding-left: 8px; }

.middle-col {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 56px !important;
  max-width: 56px !important;
}
.middle-controls {
  display: grid;
  gap: 6px;
}
.ctrl-btn {
  box-shadow: none;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  border-radius: 6px;
  display: grid;
  place-items: center;
}
.ctrl-btn:hover { background: rgba(102, 51, 153, 0.08); }

.row-with-center-controls ::v-deep(.col-sm-5) {
  flex: 0 0 calc((100% - 56px)) !important;
  max-width: calc((100% - 56px)) !important;
}
.row-with-center-controls > :first-child {
  flex-basis: calc((100% - 56px) / 2) !important;
  max-width:  calc((100% - 56px) / 2) !important;
}
.row-with-center-controls > :last-child {
  flex-basis: calc((100% - 56px) / 2) !important;
  max-width:  calc((100% - 56px) / 2) !important;
}

.pretty-item { margin: 6px 4px; cursor: pointer; }
.pretty-item--selected { background: #ede4f7; }

.list-actions {
  display: flex;
  justify-content: center;
  padding: 14px 0 4px;
}
.toggle-all {
  text-transform: none;
  font-weight: 600;
  border-color: var(--v-primary-base);
  min-width: 160px;
}
.toggle-all--active {
  background-color: #ede4f7;
  border-color: var(--v-primary-base);
}
</style>
